import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, COMMUNITY_PHOTOS } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  BookOpen,
  Building2,
  Shield,
  GraduationCap,
  AlertTriangle,
  Truck,
  Phone,
  Clock,
  MapPin,
  TrendingUp,
  DollarSign,
  Heart,
  Briefcase,
  RefreshCw
} from 'lucide-react';

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await axios.get(`${API}/resources`);
        setResources(response.data);
      } catch (error) {
        console.error('Failed to fetch resources:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  const categoryConfig = {
    clinic: { label: 'Medical', icon: Heart, color: 'bg-red-500' },
    police: { label: 'Police', icon: Shield, color: 'bg-blue-600' },
    school: { label: 'Education', icon: GraduationCap, color: 'bg-purple-500' },
    shelter: { label: 'Shelters', icon: AlertTriangle, color: 'bg-amber-500' },
    service: { label: 'Services', icon: Truck, color: 'bg-emerald-500' }
  };

  const filteredResources = activeCategory === 'all'
    ? resources
    : resources.filter(r => r.category === activeCategory);

  // PLP Progress Meter Data
  const plpAccomplishments = [
    { label: 'VAT Reduction', value: '12% to 10%', progress: 100, description: 'Reduced VAT to ease cost of living' },
    { label: 'Healthcare Investment', value: 'B$50M+', progress: 85, description: 'New facilities and equipment' },
    { label: 'Education Funding', value: 'B$30M+', progress: 90, description: 'School improvements nationwide' },
    { label: 'Infrastructure Projects', value: '25+ Projects', progress: 75, description: 'Roads, utilities, and public spaces' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Banner */}
      <div className="relative h-48 md:h-56">
        <img src={COMMUNITY_PHOTOS.gathering} alt="Killarney Community" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-purple-700/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <BookOpen className="w-10 h-10" />
              Community First
            </h1>
            <p className="mt-2 text-white/80 max-w-xl">
              Your one-stop resource hub for Killarney constituency services and information.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Local Directory */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-plp-blue" />
                Livingston Road & Beyond
              </h2>

              <Tabs value={activeCategory} onValueChange={setActiveCategory} className="space-y-4">
                <TabsList className="bg-white border flex-wrap h-auto gap-1 p-1">
                  <TabsTrigger value="all" data-testid="resource-tab-all">All</TabsTrigger>
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <TabsTrigger key={key} value={key} data-testid={`resource-tab-${key}`}>
                      <config.icon className="w-4 h-4 mr-1" />
                      {config.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value={activeCategory}>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 text-plp-blue animate-spin" />
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {filteredResources.map((resource) => {
                        const config = categoryConfig[resource.category];
                        return (
                          <Card key={resource.id} className="bg-white" data-testid={`resource-${resource.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 ${config?.color || 'bg-slate-500'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                  {config && <config.icon className="w-5 h-5 text-white" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <Badge variant="outline" className="mb-2 text-xs">
                                    {config?.label || resource.category}
                                  </Badge>
                                  <h3 className="font-heading font-semibold text-slate-900 truncate">
                                    {resource.name}
                                  </h3>
                                  <div className="mt-2 space-y-1 text-sm text-slate-600">
                                    <p className="flex items-center gap-2">
                                      <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                      <span className="truncate">{resource.address}</span>
                                    </p>
                                    {resource.phone && (
                                      <p className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                        <a href={`tel:${resource.phone}`} className="text-plp-blue hover:underline">
                                          {resource.phone}
                                        </a>
                                      </p>
                                    )}
                                    {resource.hours && (
                                      <p className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                        {resource.hours}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* PLP Progress Meter */}
            <Card className="bg-white" data-testid="plp-progress-meter">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  PLP Progress Meter
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {plpAccomplishments.map((item) => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-900">{item.label}</span>
                      <Badge className="bg-plp-gold text-plp-blue font-bold">{item.value}</Badge>
                    </div>
                    <Progress value={item.progress} className="h-2" />
                    <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Emergency Numbers */}
            <Card className="bg-red-50 border-red-200" data-testid="emergency-card">
              <CardHeader>
                <CardTitle className="font-heading text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Emergency Numbers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { label: 'Police/Fire/Ambulance', number: '911' },
                  { label: 'RBPF Non-Emergency', number: '242-919' },
                  { label: 'Crime Stoppers', number: '242-328-TIPS' }
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <a href={`tel:${item.number.replace(/-/g, '')}`} className="font-heading font-bold text-red-600">
                      {item.number}
                    </a>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Killarney at a Glance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { icon: Briefcase, label: 'Local Businesses', value: '200+' },
                  { icon: GraduationCap, label: 'Schools', value: '5' },
                  { icon: Heart, label: 'Medical Facilities', value: '3' },
                  { icon: DollarSign, label: 'Avg. Property Value', value: 'B$350K' }
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <stat.icon className="w-5 h-5 text-plp-blue" />
                    <div className="flex-1">
                      <p className="text-sm text-slate-600">{stat.label}</p>
                    </div>
                    <span className="font-heading font-bold text-slate-900">{stat.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Areas Served */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-plp-blue" />
                  Areas We Serve
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {['Sandyport', 'Olympia Heights', 'Blake Road', 'Livingston Road', 'Old Fort Bay', 'Compass Point'].map((area) => (
                    <Badge key={area} variant="outline" className="px-3 py-1">
                      {area}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesPage;
